import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  CreateNewFolder as CreateFolderIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../../utils/dateFormat';
import { trainingFilesApi } from '../../services/trainingFilesService';
import { TrainingFileMetadata } from '../../services/types';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useAuth } from '../../contexts/AuthContext';
import { TableSkeleton } from '../SkeletonLoaders';
import EmptyState from '../EmptyState';
import ErrorMessage from '../ErrorMessage';
import TrainingFileUploadDialog, { validateRelativePath } from './TrainingFileUploadDialog';
import { formatBytes } from '../../utils/formatBytes';
import {
  buildTrainingFileTree,
  sortedChildFolders,
  sortedFiles,
  isEmptyFolder,
  TrainingFileTreeNode,
} from './trainingFileTree';

interface TrainingFilesTabProps {
  workloadId: string;
}

export default function TrainingFilesTab({ workloadId }: TrainingFilesTabProps) {
  const { hasRole } = useAuth();
  const canDelete = hasRole('admin');
  const canUpload = hasRole('operator');

  const [deleteTarget, setDeleteTarget] = useState<TrainingFileMetadata | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadTargetPath, setUploadTargetPath] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Ordner-anlegen dialog state.
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderPath, setNewFolderPath] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const {
    data: files,
    loading,
    error: fetchError,
    refetch,
  } = useApiQuery<TrainingFileMetadata[]>(
    () => trainingFilesApi.list(workloadId),
    [workloadId],
    { enabled: !!workloadId }
  );

  // The tab shows the WORKLOAD TEMPLATE only — run-scoped overlays (runId set)
  // belong to the run and are surfaced on RunDetails (RunMaterialsPanel).
  const templateFiles = useMemo(
    () => (files ?? []).filter((f) => !f.runId),
    [files]
  );
  const tree = useMemo(() => buildTrainingFileTree(templateFiles), [templateFiles]);

  const toggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const openUpload = (targetPath: string) => {
    setUploadTargetPath(targetPath);
    setUploadDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      await trainingFilesApi.delete(deleteTarget.id, deleteTarget.workloadId);
      setDeleteTarget(null);
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Loeschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateFolder = async () => {
    const pathError = validateRelativePath(newFolderPath);
    if (pathError) {
      setActionError(pathError);
      return;
    }
    const trimmed = newFolderPath.trim();
    if (!trimmed) {
      setActionError('Bitte einen Ordnernamen angeben.');
      return;
    }
    setCreatingFolder(true);
    setActionError(null);
    try {
      await trainingFilesApi.createFolder({ workloadId, relativePath: trimmed });
      setFolderDialogOpen(false);
      setNewFolderPath('');
      // Reveal the new folder chain.
      setExpanded((prev) => {
        const next = new Set(prev);
        let acc = '';
        for (const seg of trimmed.replace(/\\/g, '/').split('/')) {
          if (!seg.trim()) continue;
          acc = acc ? `${acc}/${seg.trim()}` : seg.trim();
          next.add(acc);
        }
        return next;
      });
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Ordner anlegen fehlgeschlagen');
    } finally {
      setCreatingFolder(false);
    }
  };

  const displayError = actionError || fetchError;
  const isDeletingFolder = deleteTarget?.kind === 'folder';

  const renderFile = (file: TrainingFileMetadata, depth: number) => (
    <Box
      key={file.id}
      data-testid={`tf-file-${file.id}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        pl: 2 + depth * 3,
        pr: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <FileIcon fontSize="small" color="action" />
      <Typography variant="body2" sx={{ flexShrink: 0 }}>
        {file.fileName}
      </Typography>
      <Chip
        size="small"
        label={file.status === 'validated' ? 'bereit' : 'ausstehend'}
        color={file.status === 'validated' ? 'success' : 'warning'}
        variant="outlined"
        sx={{ height: 18, fontSize: '0.65rem' }}
      />
      <Typography variant="caption" color="text.secondary">
        {formatBytes(file.sizeBytes)}
      </Typography>
      {file.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {file.description}
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
        {formatDateSafe(file.uploadedAt, 'dd.MM.yyyy HH:mm')}
      </Typography>
      {canDelete && (
        <Tooltip title="Datei loeschen">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(file)}
            aria-label={`delete-${file.id}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderFolder = (node: TrainingFileTreeNode, depth: number): React.ReactNode => {
    const isOpen = expanded.has(node.path);
    const empty = isEmptyFolder(node);
    return (
      <Box key={node.path || '__root__'} data-testid={`tf-folder-${node.path}`}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.5,
            pl: 1 + depth * 3,
            pr: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => toggle(node.path)}
        >
          {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          {isOpen ? (
            <FolderOpenIcon fontSize="small" color="primary" />
          ) : (
            <FolderIcon fontSize="small" color="primary" />
          )}
          <Typography variant="body2" fontWeight={500}>
            {node.name}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {canUpload && (
            <Tooltip title="Datei in diesen Ordner hochladen">
              <IconButton
                size="small"
                aria-label={`upload-into-${node.path}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openUpload(node.path);
                }}
              >
                <UploadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && node.folderDoc && empty && (
            <Tooltip title="Leeren Ordner loeschen">
              <IconButton
                size="small"
                color="error"
                aria-label={`delete-folder-${node.path}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(node.folderDoc!);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {sortedChildFolders(node).map((child) => renderFolder(child, depth + 1))}
          {sortedFiles(node).map((file) => renderFile(file, depth + 1))}
          {empty && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: 2 + (depth + 1) * 3, py: 0.5, fontStyle: 'italic' }}
            >
              Leerer Ordner
            </Typography>
          )}
        </Collapse>
      </Box>
    );
  };

  const rootFolders = sortedChildFolders(tree);
  const rootFiles = sortedFiles(tree);
  const hasContent = rootFolders.length > 0 || rootFiles.length > 0;

  return (
    <Box data-testid="training-files-tab">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Schulungsunterlagen</Typography>
        <Box display="flex" gap={1}>
          {canUpload && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateFolderIcon />}
              onClick={() => {
                setNewFolderPath('');
                setActionError(null);
                setFolderDialogOpen(true);
              }}
            >
              Ordner anlegen
            </Button>
          )}
          {canUpload && (
            <Button
              variant="contained"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => openUpload('')}
            >
              Hochladen
            </Button>
          )}
          <Tooltip title="Aktualisieren">
            <IconButton size="small" onClick={refetch} aria-label="refresh">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Diese Ordnerstruktur ist eine <strong>Vorlage</strong>: Sie wird bei der
        Provisionierung jeder Schulungs-VM nach <code>C:\Schulungsunterlagen</code>{' '}
        ausgerollt. Leere Ordner bleiben als Struktur erhalten.
      </Alert>

      {displayError && (
        <ErrorMessage message={displayError} title="Fehler" onRetry={refetch} />
      )}

      {loading && <TableSkeleton rows={3} />}

      {!loading && !hasContent && !displayError && (
        <EmptyState
          variant="empty"
          title="Noch keine Unterlagen-Struktur"
          description="Lege Ordner an und lade PDFs, Projektdateien oder Konfigurationen hoch. Die Struktur landet bei jeder Schulung unter C:\Schulungsunterlagen."
          action={
            canUpload
              ? { label: 'Ordner anlegen', onClick: () => setFolderDialogOpen(true), icon: <CreateFolderIcon /> }
              : undefined
          }
        />
      )}

      {!loading && hasContent && (
        <Card variant="outlined">
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            {rootFolders.map((child) => renderFolder(child, 0))}
            {rootFiles.map((file) => renderFile(file, 0))}
          </CardContent>
        </Card>
      )}

      <TrainingFileUploadDialog
        open={uploadDialogOpen}
        workloadId={workloadId}
        defaultRelativePath={uploadTargetPath}
        onClose={() => setUploadDialogOpen(false)}
        onUploaded={refetch}
      />

      {/* Ordner-anlegen dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => !creatingFolder && setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ordner anlegen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Ordnerpfad"
            placeholder="z.B. Kapitel1/Uebungen"
            value={newFolderPath}
            onChange={(e) => setNewFolderPath(e.target.value)}
            disabled={creatingFolder}
            helperText='Relativ zu C:\Schulungsunterlagen. Unterordner mit "/" trennen. Kein ".." / Laufwerk.'
            inputProps={{ 'aria-label': 'Ordnerpfad' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)} disabled={creatingFolder}>
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={creatingFolder || !newFolderPath.trim()}
          >
            {creatingFolder ? 'Wird angelegt...' : 'Anlegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation (file or empty folder) */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>{isDeletingFolder ? 'Ordner loeschen' : 'Datei loeschen'}</DialogTitle>
        <DialogContent>
          <Typography>
            {isDeletingFolder ? 'Ordner ' : 'Datei '}
            <strong>{deleteTarget?.relativePath || deleteTarget?.fileName}</strong> wirklich loeschen?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Diese Aktion kann nicht rueckgaengig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Wird geloescht...' : 'Loeschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
